// store/slices/saleItemListSlice.ts
import {
  createEntityAdapter,
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit";
import { v4 } from "uuid";
import { RatingValue } from "../../common/types/enums";
import { SaleItemInterface, SaleListResponse } from "../../common/types/types";
import { RootState } from "../store";

const itemsAdapter = createEntityAdapter<SaleItemInterface>();

interface SaleListState {
  data: ReturnType<typeof itemsAdapter.getInitialState>;
  total: number;
}

const initialState: SaleListState = {
  data: itemsAdapter.getInitialState(),
  total: 0,
};

export const saleItemListSlice = createSlice({
  name: "sale-item-list",
  initialState,
  reducers: {
    // БАЗОВЫЕ
    setSaleItemList: (state, action: PayloadAction<SaleListResponse>) => {
      itemsAdapter.setAll(state.data, action.payload.data);
      state.total = action.payload.total;
    },

    upsertItems: (state, action: PayloadAction<SaleItemInterface[]>) => {
      itemsAdapter.upsertMany(state.data, action.payload);
    },

    updateItem: (
      state,
      action: PayloadAction<{
        itemId: number;
        updates: Partial<SaleItemInterface>;
      }>,
    ) => {
      const { itemId, updates } = action.payload;
      if (state.data.entities[itemId]) {
        itemsAdapter.updateOne(state.data, { id: itemId, changes: updates });
      }
    },

    // ИЗБРАННОЕ
    toggleFavorite: (
      state,
      action: PayloadAction<{ itemId: number; userId: number }>,
    ) => {
      const { itemId, userId } = action.payload;
      const item = state.data.entities[itemId];
      if (!item) return;

      const isFavorite =
        item.selected?.some((sel) => sel.userId === userId && sel.isActive) ??
        false;
      const now = new Date().toISOString();

      if (isFavorite) {
        // Удалить
        const updatedSelected =
          item.selected?.filter(
            (sel) => !(sel.userId === userId && sel.isActive),
          ) ?? [];
        itemsAdapter.updateOne(state.data, {
          id: itemId,
          changes: {
            selected: updatedSelected,
            totalFavorites: Math.max(0, (Number(item.totalFavorites) || 1) - 1),
          },
        });
      } else {
        // Добавить
        const newFavorite = {
          id: String(v4()),
          userId,
          itemId,
          selectedAt: now,
          isActive: true,
        };
        const updatedSelected = item.selected
          ? [...item.selected, newFavorite]
          : [newFavorite];
        itemsAdapter.updateOne(state.data, {
          id: itemId,
          changes: {
            selected: updatedSelected,
            totalFavorites: (Number(item.totalFavorites) || 0) + 1,
          },
        });
      }
    },

    // ОЦЕНКИ
    addRating: (
      state,
      action: PayloadAction<{
        itemId: number;
        userId: number;
        rating: RatingValue;
        comment?: string;
        files?: string[];
      }>,
    ) => {
      const { itemId, userId, rating, comment, files } = action.payload;
      const item = state.data.entities[itemId];
      if (!item) return;

      const now = new Date().toISOString();
      const newRating = {
        id: String(v4()),
        userId,
        itemId,
        rating,
        comment,
        files: files || [],
        isActive: true,
        createdAt: now,
        updatedAt: now,
      };

      const updatedAllRatings = item.allRatings
        ? [
            {
              ...newRating,
              user: { id: userId, name: "Вы", avatar: undefined },
            },
            ...item.allRatings.slice(0, 9),
          ]
        : [
            {
              ...newRating,
              user: { id: userId, name: "Вы", avatar: undefined },
            },
          ];

      itemsAdapter.updateOne(state.data, {
        id: itemId,
        changes: {
          ratings: [newRating],
          allRatings: updatedAllRatings,
          _needsRefresh: true,
        },
      });
    },

    removeRating: (
      state,
      action: PayloadAction<{ itemId: number; userId: number }>,
    ) => {
      const { itemId, userId } = action.payload;
      const item = state.data.entities[itemId];
      if (!item) return;

      itemsAdapter.updateOne(state.data, {
        id: itemId,
        changes: {
          ratings: [],
          allRatings: item.allRatings?.filter(
            (rating) => rating.userId !== userId,
          ),
          _needsRefresh: true,
        },
      });
    },

    markAsViewed: (
      state,
      action: PayloadAction<{
        itemId: number;
        userId: number;
      }>,
    ) => {
      const { itemId, userId } = action.payload;
      const item = state.data.entities[itemId];
      if (!item) return;

      const now = new Date();

      // 1. Добавляем/обновляем просмотр
      const newView = {
        id: String(v4()),
        userId,
        itemId,
        viewedAt: now.toISOString(),
        createdAt: now.toISOString(),
        updatedAt: new Date().toISOString(),
      };

      // 2. Ищем существующий просмотр этого пользователя
      const existingViewIndex =
        item.views?.findIndex((v) => v.userId === userId) ?? -1;

      let updatedViews;
      if (existingViewIndex >= 0 && item.views) {
        // Обновляем существующий
        updatedViews = [...item.views];
        updatedViews[existingViewIndex] = {
          ...updatedViews[existingViewIndex],
          viewedAt: now.toISOString(),
          updatedAt: new Date().toISOString(),
        };
      } else {
        // Добавляем новый
        updatedViews = item.views ? [newView, ...item.views] : [newView];
      }

      // 3. Увеличиваем счетчик ТОЛЬКО если это новый просмотр
      const currentTotalViews = Number(item.totalViews) || 0;
      const shouldIncrement = existingViewIndex === -1;

      itemsAdapter.updateOne(state.data, {
        id: itemId,
        changes: {
          views: updatedViews,
          totalViews: shouldIncrement
            ? currentTotalViews + 1
            : currentTotalViews,
        },
      });
    },

    // ФЛАГИ
    clearRefreshFlag: (state, action: PayloadAction<number>) => {
      const itemId = action.payload;
      const item = state.data.entities[itemId];
      if (item?._needsRefresh) {
        itemsAdapter.updateOne(state.data, {
          id: itemId,
          changes: { _needsRefresh: undefined },
        });
      }
    },
  },
});

// ЭКСПОРТ ЭКШЕНОВ
export const {
  setSaleItemList,
  upsertItems,
  updateItem,
  toggleFavorite,
  addRating,
  removeRating,
  markAsViewed,
  clearRefreshFlag,
} = saleItemListSlice.actions;

// СТАНДАРТНЫЕ СЕЛЕКТОРЫ АДАПТЕРА
export const {
  selectAll: selectAllSaleItems,
  selectById: selectSaleItemById,
  selectIds: selectAllSaleItemIds,
  selectTotal: selectTotalSaleItems,
} = itemsAdapter.getSelectors((state: RootState) => state.saleItemList.data);

// КАСТОМНЫЕ СЕЛЕКТОРЫ
export const selectSaleItemList = (state: RootState) =>
  selectAllSaleItems(state);
export const selectSaleItem = (state: RootState, id: number) =>
  selectSaleItemById(state, id);
export const selectTotalCount = (state: RootState) => state.saleItemList.total;

export const selectIsFavorite = (
  state: RootState,
  itemId: number,
  userId: number,
) => {
  const item = selectSaleItemById(state, itemId);
  return (
    item?.selected?.some((sel) => sel.userId === userId && sel.isActive) ??
    false
  );
};

export const selectUserRating = (
  state: RootState,
  itemId: number,
  userId: number,
) => {
  const item = selectSaleItemById(state, itemId);
  return item?.ratings?.find((rating) => rating.userId === userId)?.rating;
};

// СЕЛЕКТОР: ИЗБРАННЫЕ ТОВАРЫ ПОЛЬЗОВАТЕЛЯ (С МЕТАДАННЫМИ)
export const selectUserFavorites = (state: RootState, userId?: number) => {
  if (!userId) return [];

  const allItems = selectAllSaleItems(state);

  return allItems
    .filter((item) =>
      item.selected?.some(
        (sel) => sel.userId === userId && sel.isActive === true,
      ),
    )
    .map((item) => {
      const selectedRecord = item.selected?.find(
        (sel) => sel.userId === userId && sel.isActive,
      );

      return {
        id: selectedRecord?.id || String(item.id),
        itemId: item.id!,
        userId,
        selectedAt: selectedRecord?.selectedAt || new Date().toISOString(),
        item,
      };
    });
};

// СЕЛЕКТОР: ТОЛЬКО ID ИЗБРАННЫХ ТОВАРОВ
export const selectUserFavoriteIds = (state: RootState, userId?: number) => {
  if (!userId) return [];

  const allItems = selectAllSaleItems(state);

  return allItems
    .filter((item) =>
      item.selected?.some(
        (sel) => sel.userId === userId && sel.isActive === true,
      ),
    )
    .map((item) => item.id!);
};

// НОВЫЙ СЕЛЕКТОР: ТОЛЬКО ТОВАРЫ (без обертки FavoriteItemInterface)
export const selectUserFavoriteItems = (state: RootState, userId?: number) => {
  if (!userId) return [];

  const allItems = selectAllSaleItems(state);

  return allItems.filter((item) =>
    item.selected?.some(
      (sel) => sel.userId === userId && sel.isActive === true,
    ),
  );
};

export const selectHasUseStock = (state: RootState): boolean => {
  const allItems = selectAllSaleItems(state);
  return allItems.some((item) => item.useStock && item.isActive);
};

export default saleItemListSlice.reducer;
